import type { Warehouse } from '@entities/equipment';

export interface EquipmentWarehouseView {
    warehouse: Warehouse;
    inventoryItems: Warehouse['items'];
}

export const normalizeEquipmentFilter = (value: string) => value.trim().toLowerCase();

export const getFilteredInventoryItems = <TItem extends { title: string }>(
    items: TItem[],
    filterValue: string
) => {
    const normalizedFilter = normalizeEquipmentFilter(filterValue);

    if (!normalizedFilter) {
        return items;
    }

    return items.filter((inventory) => inventory.title.toLowerCase().includes(normalizedFilter));
};

export const getEquipmentWarehouseViews = (
    warehouses: Warehouse[],
    filterValue: string
): EquipmentWarehouseView[] => {
    const normalizedFilter = normalizeEquipmentFilter(filterValue);

    if (!normalizedFilter) {
        return warehouses.map((warehouse) => ({
            warehouse,
            inventoryItems: warehouse.items,
        }));
    }

    return warehouses.flatMap((warehouse) => {
        const inventoryItems = getFilteredInventoryItems(warehouse.items, normalizedFilter);

        return inventoryItems.length > 0 ? [{ warehouse, inventoryItems }] : [];
    });
};

export const getFilteredInventoryDropIndex = <TItem extends { guid: string }>({
    warehouseItems,
    visibleItems,
    activeInventoryGuid,
    visibleIndex,
}: {
    warehouseItems: TItem[];
    visibleItems: TItem[];
    activeInventoryGuid: string;
    visibleIndex: number;
}) => {
    const warehouseItemsWithoutActive = warehouseItems.filter(
        (item) => item.guid !== activeInventoryGuid
    );
    const visibleItemsWithoutActive = visibleItems.filter(
        (item) => item.guid !== activeInventoryGuid
    );
    const activeVisibleIndex = visibleItems.findIndex((item) => item.guid === activeInventoryGuid);
    const nextVisibleIndex = Math.min(Math.max(visibleIndex, 0), visibleItemsWithoutActive.length);

    if (nextVisibleIndex === 0) {
        return 0;
    }

    if (activeVisibleIndex !== -1 && nextVisibleIndex < activeVisibleIndex) {
        const nextVisibleItem = visibleItemsWithoutActive[nextVisibleIndex];
        const nextItemIndex = warehouseItemsWithoutActive.findIndex(
            (item) => item.guid === nextVisibleItem?.guid
        );

        return nextItemIndex === -1 ? 0 : nextItemIndex;
    }

    const previousVisibleItem = visibleItemsWithoutActive[nextVisibleIndex - 1];
    const previousItemIndex = warehouseItemsWithoutActive.findIndex(
        (item) => item.guid === previousVisibleItem?.guid
    );

    return previousItemIndex === -1 ? warehouseItemsWithoutActive.length : previousItemIndex + 1;
};
